import React from "react";
import { useMediaQuery } from "react-responsive";

interface SyllableMapping {
  batak_char: string;
  latin: string;
}

interface BubbleWord {
  id: number;
  batak: string;
  latin: string[];
  indonesia: string;
  syllable_mapping: SyllableMapping[];
}

interface QuestionBubbleProps {
  words: BubbleWord[];
  showPopup: string | false;
  setShowPopup: (value: string | false) => void;
  darkMode?: boolean;
}

const QuestionBubble: React.FC<QuestionBubbleProps> = ({
  words,
  showPopup,
  setShowPopup,
  darkMode = false,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <div className="relative flex items-end">
      <div
        className={`rounded-xl shadow text-center border relative ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } ${
          isMobile
            ? "px-1 py-1 min-w-[80px] max-w-[220px] pl-2"
            : "px-5 py-1 min-w-[120px] max-w-[420px] pl-4"
        }`}
        style={{
          fontFamily: "DIN Next Rounded",
          fontSize: isMobile ? 13 : 16,
        }}
      >
        {/* Bubble content */}
        <div
          className={`flex flex-wrap justify-center items-start w-full ${
            isMobile ? "gap-x-0.5 gap-y-0.5 my-2" : "gap-x-2 gap-y-1 my-5"
          }`}
        >
          {words.map((word, idx) => {
            // Blank/empty/id 999 logic
            if (word.batak === "" || word.id === 999) {
              return (
                <div
                  key={idx}
                  className="flex flex-row items-end min-w-0 max-w-full relative"
                  style={{
                    marginRight:
                      idx < words.length - 1 ? (isMobile ? "0.5em" : "1em") : 0,
                  }}
                >
                  {/* Bungkus semua suku kata agar garis bawah panjang */}
                  <span
                    className={`relative flex after:content-[''] after:absolute after:left-1/2 after:bottom-[-2px] after:-translate-x-1/2 after:w-[120%] after:border-b-2 ${
                      darkMode
                        ? "after:border-gray-500"
                        : "after:border-slate-800"
                    }`}
                  >
                    {word.syllable_mapping.map((syll, sIdx) => (
                      <span
                        key={sIdx}
                        className="flex flex-col items-center relative"
                        onMouseEnter={() => setShowPopup(`${sIdx}-${word.id}`)}
                        onMouseLeave={() => setShowPopup(false)}
                      >
                        <span
                          className="md:text-xs text-[10px] font-semibold leading-none select-none mb-1 text-transparent"
                          style={{ fontFamily: "DIN Next Rounded" }}
                        >
                          {syll.latin}
                        </span>
                        {word.batak === "" ? (
                          <span
                            className={`${
                              isMobile ? "text-lg w-10" : "text-2xl w-16"
                            } select-none text-transparent`}
                            style={{
                              fontFamily: "Noto Sans Batak",
                              display: "inline-block",
                            }}
                          >
                            blank
                          </span>
                        ) : (
                          <span
                            className={`${
                              isMobile ? "text-lg" : "text-2xl"
                            } font-bold tracking-wide w-auto select-none ${
                              darkMode ? "text-gray-100" : "text-gray-800"
                            }`}
                            style={{ fontFamily: "Noto Sans Batak" }}
                          >
                            {syll.batak_char}
                          </span>
                        )}
                      </span>
                    ))}
                  </span>
                </div>
              );
            }

            // Render each word id as a block: batak chars together, latin below, space between words
            return (
              <div
                key={idx}
                className={`flex flex-row items-end min-w-0 max-w-full relative ${
                  isMobile ? "" : ""
                }`}
                style={{
                  marginRight:
                    idx < words.length - 1 ? (isMobile ? "0.5em" : "1em") : 0,
                }}
                onMouseEnter={() => setShowPopup(`${idx}-${word.id}`)}
                onMouseLeave={() => setShowPopup(false)}
              >
                <span className="flex flex-row items-end gap-0">
                  {word.syllable_mapping.map((syll, sIdx) => (
                    <span key={sIdx} className="flex flex-col items-center">
                      <span
                        className={`md:text-xs text-[10px] font-semibold leading-none mb-1 ${
                          darkMode ? "text-blue-300" : "text-blue-700"
                        }`}
                        style={{ fontFamily: "DIN Next Rounded" }}
                      >
                        {syll.latin}
                      </span>
                      <span
                        className={`${
                          isMobile ? "text-lg" : "text-2xl"
                        } font-bold tracking-wide border-b-2 border-dashed ${
                          darkMode
                            ? "text-gray-100 border-gray-500"
                            : "text-gray-800 border-gray-400"
                        }`}
                        style={{ fontFamily: "Noto Sans Batak" }}
                      >
                        {syll.batak_char}
                      </span>
                    </span>
                  ))}
                </span>
                {/* Indonesia translation on hover */}
                {showPopup === `${idx}-${word.id}` && word.indonesia && (
                  <span
                    className={`absolute left-1/2 top-full -translate-x-1/2 rounded-md px-3 py-1 text-xs shadow z-10 whitespace-nowrap border ${
                      darkMode
                        ? "bg-gray-900 border-gray-600 text-gray-100"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                    style={{ fontFamily: "DIN Next Rounded" }}
                  >
                    {word.indonesia}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Bubble tail */}
        <div
          className={`absolute -left-2 bottom-5 ${
            isMobile ? "w-3 h-3" : "w-4 h-4"
          } rounded-sm rotate-45 ${
            darkMode
              ? "bg-gray-800 border-l border-b border-gray-700"
              : "bg-white border-l border-b border-gray-200"
          }`}
        ></div>
      </div>
    </div>
  );
};

export default QuestionBubble;
